import { useMemo } from "react";

import { useQueries } from "@tanstack/react-query";

import {
  keyMaker,
  PhysicalAccount,
} from "@fartherfinance/frontend/api/Accounts/hooks/usePhysicalAccount";
import {
  HookResult,
  PhysicalAccountId,
} from "@fartherfinance/frontend/api/Types";

function isPhysicalAccount(account: any): account is PhysicalAccount {
  return account !== undefined && "physicalAccountId" in account;
}

type AllPhysicalAccountsData = HookResult<PhysicalAccount[]>;

function useGetAllPhysicalAccounts(
  physicalAccountIds: PhysicalAccountId[]
): AllPhysicalAccountsData {
  const physicalAccountsQueryKeys = useMemo(
    () => physicalAccountIds.map((tpi) => keyMaker(tpi)),
    [physicalAccountIds]
  );

  const userQueries = useQueries({
    queries: (physicalAccountsQueryKeys ?? []).map((queryKey) => {
      return {
        queryKey: queryKey,
      };
    }),
  });

  const physicalAccountsFetching = useMemo(
    () => userQueries.some((query) => query.isFetched === false),
    [userQueries]
  );

  const physicalAccountsError = useMemo(
    () => userQueries.some((query) => query.isError === true),
    [userQueries]
  );

  return useMemo(() => {
    if (physicalAccountsFetching) {
      return {
        isLoading: true,
        data: undefined,
        hasError: false,
      };
    }

    if (physicalAccountsError) {
      return {
        isLoading: false,
        data: undefined,
        hasError: true,
        error: new Error("Error fetching physical accounts"),
      };
    }

    const allPhysicalAccounts = userQueries
      .map((query) => query.data)
      .filter(isPhysicalAccount);

    return {
      isLoading: false,
      data: allPhysicalAccounts,
      hasError: false,
    };
  }, [physicalAccountsFetching, physicalAccountsError, userQueries]);
}

export default useGetAllPhysicalAccounts;
